import { useMutation } from '@apollo/client';
import React from 'react';
import { match } from 'ts-pattern';

import { SimpleTooltip } from '@eluve/components';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Button,
  Icon,
  Text,
  useToast,
} from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';

// Single mutation that toggles isActive status
const togglePromptTemplateModelMutation = graphql(`
  mutation TogglePromptTemplateModel(
    $promptTemplateId: uuid!
    $promptTemplateModelId: uuid!
    $isActive: Boolean!
  ) {
    updateLlmModelPromptTemplates(
      where: {
        _and: [
          { id: { _eq: $promptTemplateModelId } }
          { promptTemplateId: { _eq: $promptTemplateId } }
        ]
      }
      _set: { isActive: $isActive }
    ) {
      affectedRows
      returning {
        __typename
        id
        isActive
      }
    }
  }
`);

export interface TogglePromptTemplateModelActionProps {
  promptTemplateId: string;
  isDefault: boolean;
  isActive: boolean;
  id: string;
}

export const TogglePromptTemplateModelAction: React.FC<
  TogglePromptTemplateModelActionProps
> = ({ promptTemplateId, isDefault, isActive, id }) => {
  const toast = useToast();
  // Single mutation hook for toggling
  const [toggleModel, { loading }] = useMutation(
    togglePromptTemplateModelMutation,
    {
      onCompleted: (data) => {
        if (data.updateLlmModelPromptTemplates?.affectedRows !== 1) {
          toast.error(
            `Failed to ${isActive ? 'deactivate' : 'activate'} model for this prompt`,
          );
          return;
        }

        toast.success(
          `Successfully ${isActive ? 'deactivated' : 'activated'} model for this prompt`,
        );
      },
      onError: () =>
        toast.error(
          `Failed to ${isActive ? 'deactivate' : 'activate'} model for this prompt`,
        ),
      optimisticResponse: ({ isActive: newIsActiveValue }) => ({
        updateLlmModelPromptTemplates: {
          affectedRows: 1,
          returning: [
            {
              __typename: 'LlmModelPromptTemplates' as const,
              id: id,
              isActive: newIsActiveValue,
            },
          ],
        },
      }),
    },
  );

  const handleToggle = async () => {
    const newActiveState = !isActive;

    await toggleModel({
      variables: {
        promptTemplateId,
        promptTemplateModelId: id,
        isActive: newActiveState,
      },
    });
  };

  const { label, description, action, Component } = match(isActive)
    .with(true, () => {
      const description = (
        <div>
          <Text>
            Marking this model as inactive will make it unavailable to be used
            with prompts
          </Text>
        </div>
      );

      return {
        label: 'Mark this model args as inactive',
        description,
        action: 'Deactivate',
        Component: (
          <Icon
            name="PackageMinus"
            size="xl"
            className="text-bg-orange-strong"
          />
        ),
      };
    })
    .otherwise(() => ({
      label: 'Mark this model as active',
      description: (
        <Text>
          Marking this model as active will make it available to be used with
          prompts
        </Text>
      ),
      action: 'Activate',
      Component: (
        <Icon
          name="PackagePlus"
          size="xl"
          className="text-bg-lightgreen-strong"
        />
      ),
    }));

  if (isDefault) {
    return null;
  }

  return (
    <AlertDialog>
      <SimpleTooltip label={label}>
        <AlertDialogTrigger asChild>
          <Button variant="outline" disabled={loading}>
            {Component}
          </Button>
        </AlertDialogTrigger>
      </SimpleTooltip>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Confirm</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel autoFocus>Cancel</AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            onClick={handleToggle}
            disabled={loading}
          >
            {action}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
